import { Dispatch, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import {
  IRequirementStructuralUnitItem,
  IRequirementStructuralUnitRaw,
  IRequirementStructuralUnitType,
} from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ISuEditorForm } from 'KnmPackage/StructureRequirementsNpaModule/interface';
import { requirementStructuralUnitTypes } from 'KnmPackage/RequirementsNpaModalModule/components/requirementStructuralUnitTypes';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import styles from './CreateSuAndMandatoryModal.module.scss';
import { AutoSelectSuElement } from './AutoSelectSuElement';

interface ICreateSuAndMandatoryModalFormProps {
  onClose: () => void;
  npaItem: IRequirementNpa;
  withRequirements: boolean;
  disabledEditSu: boolean;
}

export const CreateSuAndMandatoryModalForm = ({
  handleSubmit,
  form,
  values,
  npaItem,
  onClose,
  invalid,
  withRequirements,
  disabledEditSu,
}: FormRenderProps<ISuEditorForm> & ICreateSuAndMandatoryModalFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const addStructuralUnit = () => {
    const newObj: IRequirementStructuralUnitRaw = {
      components: [{ selectType: null, type: '', value: '', uuid: uuidv4() }],
    };

    if (withRequirements) {
      newObj.mandatoryRequirements = [''];
    }

    form.mutators.push?.(`newStructuralUnits`, newObj);
  };

  const addStructuralUnitItem = (
    structuralUnitName: string,
    components: IRequirementStructuralUnitItem[] | undefined,
  ) => {
    if (components?.length === 10) return;

    const structuralUnitItem: IRequirementStructuralUnitItem = {
      selectType: null,
      type: '',
      value: '',
      uuid: uuidv4(),
    };

    form.mutators.push?.(`${structuralUnitName}.components`, structuralUnitItem);
  };

  const addMandatoryItem = (structuralUnitName: string) => {
    form.mutators.push?.(`${structuralUnitName}.mandatoryRequirements`, '');
  };

  const removeSu = useCallback(
    (suIndex: number, suRemove: (index: number) => void, scompRemove: () => void) => {
      if (values.newStructuralUnits[suIndex].components?.length === 1) {
        if (values.newStructuralUnits.length > 1) {
          suRemove(suIndex);
        }
      } else scompRemove();
    },
    [values],
  );

  const availableOptions = useCallback((components: IRequirementStructuralUnitItem[] | undefined) => {
    return requirementStructuralUnitTypes.filter(i => !components?.find(j => j.type === i.value));
  }, []);

  return (
    <form
      onSubmit={event => {
        if (invalid) {
          dispatch(
            addNoticeAction({
              renderContent: 'Требуется исправить ошибки',
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.CREATE_SU_ERROR,
            }),
          );
        }

        handleSubmit(event);
      }}
    >
      <ModalHeader
        title={
          disabledEditSu
            ? 'Создание обязательных требований'
            : `Создание структурных единиц${withRequirements ? ' и обязательных требований' : ''}`
        }
      />
      <div className={styles.Wrapper} style={{ overflow: !withRequirements ? 'unset' : 'auto' }}>
        <KnmCollapse
          title={
            <div className={styles.CollapseWrapper}>
              <span>
                НПА № <b>{npaItem.number}</b>
              </span>
              <span>
                Дата{' '}
                <b>
                  {formatDate({
                    date: npaItem.acceptanceDate || '',
                    dateFormat: DATE_FORMAT_LONG_FNS,
                    parseFormat: SYSTEM_DATE_FORMAT_FNS,
                  })}
                </b>
              </span>
            </div>
          }
        >
          <div className={styles.NpaItemTitle}>{npaItem.title}</div>
        </KnmCollapse>
        <Row justify="start">
          <span>Структурные единицы НПА, содержащего обязательные требования</span>
          {!disabledEditSu && (
            <div className={styles.ButtonCreateUnitWrapper}>
              <Button
                text="Добавить структурную единицу"
                variant="link"
                className={styles.CreateUnitButton}
                onClick={addStructuralUnit}
                type="button"
              />
            </div>
          )}
        </Row>
        <div className={styles.GeneralWrapper}>
          <FieldArray name={'newStructuralUnits'}>
            {({ fields }) => {
              return fields.map((name, i) => {
                return (
                  <div key={name} className={styles.ComponentsWrapper}>
                    <div className={styles.ComponentsInnerWrapper}>
                      <FieldArray name={`${name}.components`}>
                        {({ fields: compFields }) =>
                          compFields.map((compName, compIndex) => {
                            const fieldValue: IRequirementStructuralUnitItem = compFields.value?.[compIndex];

                            return (
                              <div key={fieldValue.uuid} className={styles.ComponentWrapper}>
                                <AutoSelectSuElement
                                  name={`${compName}.selectType`}
                                  fieldValue={fieldValue}
                                  disabled={disabledEditSu}
                                  onChange={(value: IRequirementStructuralUnitType) => {
                                    // @ts-ignore
                                    form.change(`${compName}.type` as keyof ISuEditorForm, value ? value.value : '');
                                  }}
                                  options={availableOptions(values.newStructuralUnits[i].components)}
                                />
                                <TextInputField
                                  name={`${compName}.value`}
                                  placeholder="Значение"
                                  className={styles.Field}
                                  disabled={disabledEditSu}
                                />
                                {!disabledEditSu && (
                                  <div className={styles.DeleteButtonBox}>
                                    <button
                                      type="button"
                                      onClick={() => removeSu(i, fields.remove, () => compFields.remove(compIndex))}
                                      className={styles.DeleteButton}
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        }
                      </FieldArray>
                      {!disabledEditSu && (
                        <>
                          <div className={styles.DeleteAllItemBox}>
                            <CloseButton
                              className="shared-deleting-row-btn"
                              onClick={() => values.newStructuralUnits.length > 1 && fields.remove(i)}
                            />
                          </div>
                          <Button
                            text="Добавить"
                            variant="link"
                            className={styles.CreateUnitButton}
                            onClick={() => addStructuralUnitItem(name, values.newStructuralUnits[i].components)}
                            type="button"
                            disabled={values.newStructuralUnits[i].components?.length === 10}
                          />
                        </>
                      )}
                    </div>
                    {withRequirements && (
                      <>
                        <Row justify="start" margin="none">
                          <span>Обязательные требования</span>
                          <div className={styles.ButtonCreateMandatoryWrapper}>
                            <Button
                              text="Добавить формулировку"
                              variant="link"
                              className={styles.CreateUnitButton}
                              startIcon={styles.AddIcon}
                              onClick={() => addMandatoryItem(name)}
                              type="button"
                            />
                          </div>
                        </Row>
                        <FieldArray name={`${name}.mandatoryRequirements`}>
                          {({ fields: mandatoryFields }) =>
                            mandatoryFields.map((mandatoryName, mandatoryIndex) => (
                              <div className={styles.MandatoryItemWrapper} key={mandatoryName}>
                                <TextareaField
                                  name={mandatoryName}
                                  inputClassName={styles.MandatoryNameTextarea}
                                  placeholder={'Введите текст формулировки обязательного требования'}
                                  resize="none"
                                />
                                <CloseButton
                                  className={`shared-deleting-row-btn ${styles.CloseButtonMandatory}`}
                                  onClick={() =>
                                    (values.newStructuralUnits[i].mandatoryRequirements || []).length > 1 &&
                                    mandatoryFields.remove(mandatoryIndex)
                                  }
                                />
                              </div>
                            ))
                          }
                        </FieldArray>
                      </>
                    )}
                  </div>
                );
              });
            }}
          </FieldArray>
        </div>
      </div>
      <ModalActions>
        <Button text="Сохранить" size="medium" color="primary" type="submit" />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
    </form>
  );
};
